import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Menu,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Tooltip,
  Stack
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import { fetchFiles } from "../redux/actions/fileActions";
import FinanceStatsById from "../components/GetFinanceStatsById";
import SalesPaymentPopup from "../components/SalesPaymentPopup";
import { getSalesDocumentsByCaseId } from "../redux/actions/salesDocumentActions";
import { getSalesPaymentsByCaseId } from "../redux/actions/salesPaymentActions";
import axios from "../utils/axiosConfig";
import { verticalTabStyles } from "../styles/verticalTabStyles";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { smallTitle } from "../styles/typography";
import Avatar from "@mui/material/Avatar";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { formatDate } from "../utils/dateUtils";

const tableStyles = windowsTableStyles();
const FinanceGraph = ({ onTabChange }) => {
  const { t, i18n } = useTranslation(['common', 'pages', 'buttons']);
  const { files = [] } = useSelector((state) => state.fileList);
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { salesDocuments = [] } = useSelector(
    (state) => state.salesDocumentList
  );
  const { salesPayments = [] } = useSelector((state) => state.salesPaymentList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state

  useEffect(() => {
    dispatch(fetchFiles());
    dispatch(getSalesDocumentsByCaseId(id));
    dispatch(getSalesPaymentsByCaseId(id));
  }, [dispatch, id]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    onTabChange(newValue);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    const currentFile = files.find((file) => file._id === id);
    const from = currentFile ? currentFile.officeMembers[0].member : null;
    const to = currentFile ? currentFile.parties[0].name : null;
    navigate(path, { state: { caseId: id, from, to } });
    handleMenuClose();
  };

  const [popupData, setPopupData] = useState({
    caseId: "",
    from: null,
    to: null,
  });

  const handleOpen = () => {
    const currentFile = files.find((file) => file._id === id);
    const from = currentFile ? currentFile.officeMembers[0].member : null;
    const to = currentFile ? currentFile.parties[0].name : null;
    setPopupData({ caseId: id, from, to });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  const tableHeaderStyles = {
    backgroundColor: "#f5f5f5",
    color: "#333",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    padding: "10px 15px",
  };

  const userFiles = files.filter((file) => {
    if (user?.role === "Manager") {
      return true; // Managers can see all files
    } else if (user?.role === "Responsible Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
      );
    } else if (user?.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  const [filteredSalesDocuments, setFilteredSalesDocuments] = useState([]);

  useEffect(() => {
    if (salesDocuments) {
      const filtered = salesDocuments
        .filter((salesDocument) => id === salesDocument.case?._id)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setFilteredSalesDocuments(filtered);
    }
  }, [salesDocuments, id]);

  // useEffect(() => {
  //   console.log("id is ", id);
  // }, [id]);


  const [filteredSalesPayments, setFilteredSalesPayments] = useState([]);

  useEffect(() => {
    if (salesPayments) {
      const filtered = salesPayments
        .filter((salesPayment) => id === salesPayment.case?._id)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setFilteredSalesPayments(filtered);
    }
  }, [salesPayments, id]);

  const handleViewDocument = async (id) => {
    try {
      // Get current language from i18n
      const currentLanguage = i18n?.language || 'en';
      const languageParam = currentLanguage.startsWith('nl') ? 'nl' : 'en';
      const response = await axios.get(`/api/salesDocuments/${id}/download?language=${languageParam}`);
      const { fileUrl } = response.data;

      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        fileUrl
      )}&embedded=true`;
      window.open(viewerUrl, "_blank");
    } catch (error) {
      console.error("Error viewing the document:", error);
    }
  };

  const handleDownload = async (id) => {
    try {
      // Get current language from i18n
      const currentLanguage = i18n?.language || 'en';
      const languageParam = currentLanguage.startsWith('nl') ? 'nl' : 'en';
      const response = await axios.get(`/api/salesDocuments/${id}/download?language=${languageParam}`);
      const { fileUrl } = response.data;
      window.open(fileUrl, "_blank");
    } catch (error) {
      console.error("Error downloading the document:", error);
    }
  };

  const handleSendMessage = async (id) => {
    try {
      // Step 1: Get the pre-signed URL for the document
      // Get current language from i18n
      const currentLanguage = i18n?.language || 'en';
      const languageParam = currentLanguage.startsWith('nl') ? 'nl' : 'en';
      const response = await axios.get(`/api/salesDocuments/${id}/download?language=${languageParam}`);
      const { fileUrl } = response.data;

      // Step 2: Fetch the file using the pre-signed URL
      const fileResponse = await axios.get(fileUrl, { responseType: "blob" });
      const file = new File([fileResponse.data], `${id}.docx`, {
        type: fileResponse.data.type,
      });

      // Step 3: Convert the file to base64
      const base64Content = await convertFileToBase64(file);

      // Step 4: Navigate to the SendEmail page with the file as an attachment
      navigate("/send-email", {
        state: { attachments: [{ file, base64Content, name: file.name }] },
      });
    } catch (error) {
      console.error("Error fetching the document:", error);
    }
  };

  const handleViewInvoice = (invoiceId) => {
    // const currentFile = files.find(file => file._id === id);
    // const owner = currentFile ? currentFile.officeMembers[0].member : null;
    // console.log("owner", owner);
    navigate(`/sales-documents/${invoiceId}`, { state: { caseId: id } });
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };


  const stringToColor = (string) => {
    if (!string) return "#ccc"; // Default gray color if string is empty

    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }

    return color;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "#85bc20"; // Green color for Paid status
      case "unpaid":
        return "#e5202e"; // Red color for Unpaid status
      case "partially paid":
        return "orange"; // Orange color for Partially Paid status
      default:
        return "gray"; // Default color in case of unknown status
    }
  };

  return (
    <Box sx={{ backgroundColor: "white", padding: "32px 24px " }}>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "white",
          // marginTop: "24px",
          border: "1px solid #d1d1d1",
        }}
      >
        <Box sx={{ width: "200px", borderRight: "1px solid #d1d1d1" }}>
          <Box sx={{ marginBottom: "40px", marginTop: "30px" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              orientation="vertical"
              aria-label="finance tabs"
              TabIndicatorProps={{
                sx: {
                  left: 0, // Position the indicator at the start
                },
              }}
              sx={verticalTabStyles}
            >
              <Tab label={t('common:overview')} />
              <Tab label={t('pages:finance.sales_documents')} />
              <Tab label={t('pages:finance.sales_payments')} />
            </Tabs>
          </Box>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            padding: "48px 32px 32px 32px",
            width: "calc(100% - 200px)",
          }}
        >
          {activeTab === 0 && (
            <Box>
              <FinanceStatsById id={id} />
            </Box>
          )}
          {activeTab === 1 && (
            <Box>
              {filteredSalesDocuments.length > 0 ? (
                <Table sx={tableStyles.container} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableStyles.headerCell}>#</TableCell>
                      <TableCell sx={tableStyles.headerCell}>
                        {t('common:invoice_number')}
                      </TableCell>
                      <TableCell sx={tableStyles.headerCell}>Type</TableCell>
                      <TableCell sx={tableStyles.headerCell}>From</TableCell>
                      <TableCell sx={tableStyles.headerCell}>To</TableCell>
                      <TableCell sx={tableStyles.headerCell}>Due Date</TableCell>

                      <TableCell sx={tableStyles.headerCell}>Status</TableCell>
                      <TableCell sx={tableStyles.headerCell}>Total Due</TableCell>
                      <TableCell sx={tableStyles.headerCell}>
                        Total Amount
                      </TableCell>
                      <TableCell sx={tableStyles.headerCell}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSalesDocuments
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((doc, index) => (
                        <TableRow key={doc._id} sx={{ ...tableStyles.row }}>
                          <TableCell sx={tableStyles.cell}>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell sx={tableStyles.cell}>
                            {doc.sequenceNumber || doc._id.slice(-6)}
                          </TableCell>
                          <TableCell sx={tableStyles.cell}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <InsertDriveFileIcon
                                sx={{
                                  fontSize: 13,
                                  color:
                                    doc.type === "invoice"
                                      ? "rgb(133, 188, 32) !important"
                                      : doc.type === "credit"
                                        ? "rgb(66, 94, 16) !important"
                                        : "rgb(114, 16, 23) !important",
                                  marginRight: "-2.5px"
                                }}
                              />
                              <span
                                onClick={() => {
                                  if (doc.type === 'invoice') {
                                    navigate(`/invoice/${doc?._id}`, { state: { caseId: id } });
                                  } else if (doc.type === 'credit') {
                                    navigate(`/invoice/${doc?._id}`, { state: { caseId: id } });
                                  } else if (doc.type === 'provision note') {
                                    navigate(`/invoice/${doc?._id}`, { state: { caseId: id } });
                                  } 
                                }}
                                style={{ 
                                  cursor:  'pointer',
                                  textDecoration: 'underline'
                                }}
                              >
                                {truncateText(doc.type, 20)}
                              </span>
                            </Box>
                          </TableCell>


                          <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} onClick={() => { navigate(`/relationships/${doc?.from?._id}`); }}>
                            {doc.from ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar
                                  alt={doc.from.firstName}
                                  src={
                                    doc.from.avatar
                                      ? doc.from.avatar
                                      : "/static/images/avatar/1.jpg" // Default avatar if none is provided
                                  }
                                  sx={{
                                    width: "1rem", // Set width to 1rem
                                    height: "1rem", // Set height to 1rem
                                    fontSize: "8px", // Set font size to 8px

                                  }}
                                >
                                  {/* Display initials if no avatar */}
                                  {!doc.from.avatar &&
                                    doc.from.firstName &&
                                    doc.from.lastName
                                    ? doc.from.firstName.charAt(0) + doc.from.lastName.charAt(0) // First and last initial
                                    : !doc.from.avatar && doc.from.firstName
                                      ? doc.from.firstName.charAt(0) // Only first initial
                                      : !doc.from.avatar && doc.from.lastName
                                        ? doc.from.lastName.charAt(0) // Only last initial
                                        : ""}
                                </Avatar>
                                <span>
                                  {`${doc.from.firstName || ""} ${doc.from.middleName || ""} ${doc.from.lastName || ""}`.trim()}
                                </span>
                              </Box>
                            ) : (
                              "                              t('common:no_from')"
                            )}
                          </TableCell>



                          <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} onClick={() => { navigate(`/relationships/${doc?.to?._id}`); }}>
                            {doc.to ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar
                                  alt={
                                    doc.to.contractType !== "Individual"
                                      ? doc.to.businessName
                                      : `${doc.to.firstName} ${doc.to.lastName}`
                                  }
                                  src={
                                    doc.to.avatar
                                      ? doc.to.avatar
                                      : "/static/images/avatar/1.jpg" // Default avatar if none is provided
                                  }
                                  sx={{
                                    width: "1rem", // Set width to 1rem
                                    height: "1rem", // Set height to 1rem
                                    fontSize: "8px", // Set font size to 8px

                                  }}
                                >
                                  {/* Display initials if no avatar */}
                                  {!doc.to.avatar &&
                                    doc.to.firstName &&
                                    doc.to.lastName
                                    ? doc.to.firstName.charAt(0) + doc.to.lastName.charAt(0) // First and last initial
                                    : !doc.to.avatar && doc.to.firstName
                                      ? doc.to.firstName.charAt(0) // Only first initial
                                      : !doc.to.avatar && doc.to.lastName
                                        ? doc.to.lastName.charAt(0) // Only last initial
                                        : ""}
                                </Avatar>
                                <span>
                                  {doc.to.contractType !== "Individual"
                                    ? `${doc.to.businessName || ""}`
                                    : `${doc.to.firstName || ""} ${doc.to.middleName ? doc.to.middleName + " " : ""}${doc.to.lastName || ""}`}
                                </span>
                              </Box>
                            ) : (
                              "                              t('common:no_to')"
                            )}
                          </TableCell>



                          <TableCell sx={tableStyles.cell}>
                            {formatDate(doc.dueDate)}
                          </TableCell>

                          <TableCell sx={tableStyles.cell}>
                            <span
                              style={{
                                padding: "1px 6px",
                                borderRadius: "20px", // To make it rounded
                                backgroundColor: getStatusColor(doc.status), // Dynamically assign color
                                color: doc?.status === "paid" ? "black" : "white", // White text color 
                                fontSize: "8.25px",
                                fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                fontWeight: "700",
                                textTransform: "upperCase",
                                height: "12px",
                                letterSpacing: "0.2em"
                              }}
                            >
                              {doc.status}
                            </span>
                          </TableCell>

                          <TableCell sx={tableStyles.cell}>{doc.totalDue.toFixed(2)}</TableCell>
                          <TableCell sx={tableStyles.cell}>{doc.total.toFixed(2)}</TableCell>

                          <TableCell sx={tableStyles.cell}>
                            <Box display="flex">
                              <Tooltip title={t('common:view_document')}>
                                <IconButton
                                  onClick={() => handleViewDocument(doc._id)}
                                  sx={{ width: "20px", height: "20px", marginX: "8px", color: "#005b92", }}
                                >
                                  <VisibilityIcon sx={{ fontSize: "20px" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('common:download')}>
                                <IconButton onClick={() => handleDownload(doc._id)} sx={{ width: "20px", height: "20px", marginX: "8px", color: "#005b92", }}>
                                  <DownloadIcon sx={{ fontSize: "20px" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('common:send_mail')}>
                                <IconButton
                                  onClick={() => handleSendMessage(doc._id)}
                                  sx={{ width: "20px", height: "20px", marginX: "8px", color: "#005b92", }}
                                >
                                  <SendIcon sx={{ fontSize: "20px" }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>

                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <Box
                  sx={{ ...smallTitle, padding: "32px 24px", textAlign: "center" }}
                >
                  {t('common:no_data_available')}
                </Box>
              )}
              <TablePagination
                component="div"
                count={filteredSalesDocuments.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          )}
          {activeTab === 2 && (
            <Box>
              {filteredSalesPayments.length > 0 ? (
                <Table sx={tableStyles.container} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableStyles.headerCell}>#</TableCell>
                      <TableCell sx={tableStyles.headerCell}>Type</TableCell>
                      <TableCell sx={tableStyles.headerCell}>
                        {t('common:invoice_number')}
                      </TableCell>
                      <TableCell sx={tableStyles.headerCell}>Doc. Type</TableCell>
                      <TableCell sx={tableStyles.headerCell}>From</TableCell>
                      <TableCell sx={tableStyles.headerCell}>To</TableCell>
                      <TableCell sx={tableStyles.headerCell}>Date</TableCell>
                      <TableCell sx={tableStyles.headerCell}>Amount</TableCell>
                      {/* <TableCell sx={tableStyles.headerCell}>Doc. Id</TableCell> */}
                      <TableCell sx={tableStyles.headerCell}>
                        Doc. Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSalesPayments
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((payment, index) => (
                        <TableRow key={payment._id} sx={{ ...tableStyles.row, }}>
                          <TableCell sx={tableStyles.cell}>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell sx={tableStyles.cell}>

                            {truncateText(payment.type, 20)}

                          </TableCell>

                          <TableCell sx={tableStyles.cell}>
                            {payment?.salesDocument?.sequenceNumber ||
                              payment?.salesDocument?._id?.slice(-6) ||
                              "-"}
                          </TableCell>
                          <TableCell sx={tableStyles.cell}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <InsertDriveFileIcon
                                sx={{
                                  fontSize: 13,
                                  color:
                                    payment?.salesDocument?.type === "invoice"
                                      ? "rgb(133, 188, 32) !important"
                                      : payment?.salesDocument?.type === "credit"
                                        ? "rgb(66, 94, 16) !important"
                                        : "rgb(114, 16, 23) !important",
                                  marginRight: "-2.5px"
                                }}
                              />
                              <span
                                onClick={() => {
                                  if (payment?.salesDocument?.type === 'invoice') {
                                    navigate(`/invoice/${payment?.salesDocument?._id}`, { state: { caseId: id } });
                                  } else if (payment?.salesDocument?.type === 'credit') {
                                    navigate(`/credit/${payment?.salesDocument?._id}`, { state: { caseId: id } });
                                  } else if (payment?.salesDocument?.type === 'provision note') {
                                    navigate(`/provision/${payment?.salesDocument?._id}`, { state: { caseId: id } });
                                  } 
                                }}
                                style={{
                                  cursor: 'pointer',
                                  textDecoration: 'underline' 
                                }}
                              >
                                {truncateText(payment?.salesDocument?.type, 20)}
                              </span>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} onClick={() => { navigate(`/relationships/${payment?.from?._id}`); }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar
                                alt={`${payment.from.firstName} ${payment.from.lastName}`}
                                src={payment.from.avatar ? payment.from.avatar : "/static/images/avatar/1.jpg"}
                                sx={{
                                  width: "1rem", // Set width to 1rem
                                  height: "1rem", // Set height to 1rem
                                  fontSize: "8px", // Set font size to 8px
                                  bgcolor: !payment.from.avatar
                                    ? stringToColor(`${payment.from.firstName} ${payment.from.lastName}`)
                                    : "transparent", // Random color if no avatar
                                }}
                              >
                                {/* Display initials if no avatar */}
                                {!payment.from.avatar &&
                                  payment.from.firstName &&
                                  payment.from.lastName
                                  ? payment.from.firstName.charAt(0) + payment.from.lastName.charAt(0) // First and last initial
                                  : !payment.from.avatar && payment.from.firstName
                                    ? payment.from.firstName.charAt(0) // Only first initial
                                    : !payment.from.avatar && payment.from.lastName
                                      ? payment.from.lastName.charAt(0) // Only last initial
                                      : ""}
                              </Avatar>
                              {/* Display Name */}
                              <span>
                                {payment.from
                                  ? `${payment.from.firstName} ${payment.from.middleName ? payment.from.middleName + " " : ""}${payment.from.lastName}`
                                  : t('common:no_from')}
                              </span>
                            </Box>
                          </TableCell>



                          <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} onClick={() => { navigate(`/relationships/${payment?.to?._id}`); }}>
                            {payment.to ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar
                                  alt={
                                    payment.to.contractType !== "Individual"
                                      ? payment.to.businessName
                                      : `${payment.to.firstName} ${payment.to.lastName}`
                                  }
                                  src={
                                    payment.to.avatar ? payment.to.avatar : "/static/images/avatar/1.jpg" // Default avatar if none is provided
                                  }
                                  sx={{
                                    width: "1rem", // Set width to 1rem
                                    height: "1rem", // Set height to 1rem
                                    fontSize: "8px", // Set font size to 8px
                                    backgroundColor: stringToColor(
                                      payment.to.contractType !== "Individual"
                                        ? payment.to.businessName
                                        : `${payment.to.firstName} ${payment.to.lastName}`
                                    ), // Use random color function for background
                                  }}
                                >
                                  {!payment.to.avatar &&
                                    payment.to.firstName &&
                                    payment.to.lastName
                                    ? payment.to.firstName.charAt(0) + payment.to.lastName.charAt(0) // Show both initials
                                    : !payment.to.avatar && payment.to.firstName
                                      ? payment.to.firstName.charAt(0) // Show first initial if no avatar
                                      : !payment.to.avatar && payment.to.lastName
                                        ? payment.to.lastName.charAt(0) // Show last initial if no avatar
                                        : ""}
                                </Avatar>
                                <span>
                                  {payment.to.contractType !== "Individual"
                                    ? payment.to.businessName || ""
                                    : `${payment.to.firstName || ""} ${payment.to.middleName ? payment.to.middleName + " " : ""}${payment.to.lastName || ""}`}
                                </span>
                              </Box>
                            ) : (
                              "                              t('common:no_to')"
                            )}
                          </TableCell>




                          <TableCell sx={tableStyles.cell}>
                            {formatDate(payment.date)}
                          </TableCell>
                          {/* <TableCell sx={{ ...tableStyles.cell, "&:hover": { cursor: 'pointer', textDecoration: "underline" }, color: "#005b92", }}
                            onClick={() => navigate(`/files/${payment?.case?._id
                              }/overview`)}>
                            {payment.case ? (
                              <>
                                <Stack direction="row">
                                  <Typography
                                    variant="caption"

                                    sx={{ fontSize: "0.7rem" }} // Smaller font size
                                  >
                                    {payment.case.fileNo}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: "bold", fontSize: "0.8rem", marginTop: "-2px", marginLeft: "4px", marginTop:"-2px" }} // Smaller font size
                                  >
                                    {truncateText(payment.case.name, 30)}
                                  </Typography>
                                </Stack>
                              </>
                            ) : (
                              "No case"
                            )}
                          </TableCell> */}
                          <TableCell sx={tableStyles.cell}>{payment.amount.toFixed(2)}</TableCell>
                          {/* <TableCell sx={tableStyles.cell}>{payment.salesDocument._id}</TableCell> */}

                          <TableCell sx={tableStyles.cell}>
                            {payment.salesDocumentTotal.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <Box
                  sx={{ ...smallTitle, padding: "32px 24px", textAlign: "center" }}
                >
                  {t('common:no_data_available')}
                </Box>
              )}
              <TablePagination
                component="div"
                count={filteredSalesPayments.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
              <SalesPaymentPopup
                open={open}
                onClose={handleClose}
                popupData={popupData}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FinanceGraph;